import { FC, useCallback, useEffect, useState } from "react";
import styles from "./HomeCards.module.scss";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Header, LoadingError } from "@kaladel/react/Components";
import { classNames } from "@kaladel/react/Utilities";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router";
import { DisplayCurrency } from "../DisplayCurrency";
import { DisplayPercentage } from "../DisplayPercentage";
import { MortgagesOverviewGetResponse } from "ApiClient/models";
import { generateMortgageChartData } from "Helpers/GenerateChartData";
import { useApp } from "../../AppContext";
import { useApiClient } from "Contexts/ApiClientContext";

export const MortgagesCard: FC = () => {
    const { api, apiCall } = useApiClient();
    const { activeCurrency } = useApp();

    const [mortgagesOverview, setMortgagesOverview] = useState<MortgagesOverviewGetResponse | null>(null);

    const refreshMortgagesOverview = async () => {
        const response = await apiCall(() => api.mortgages.overview.get());

        if (response.kind === "success") {
            setMortgagesOverview(response.value);
        }
    };

    const getMortgagesData = useCallback(() => {
        if (!activeCurrency || !mortgagesOverview) return null;

        const thisCurrency = mortgagesOverview.balances.find(b => b.currencyId === activeCurrency);

        const hasAccount = thisCurrency != null;

        const totals = {
            balance: thisCurrency?.totalBalance ?? 0,
            equity: thisCurrency?.totalEquity ?? 0,
            propertyValue: thisCurrency?.totalPropertyValue ?? 0
        };

        return {
            hasAccount,
            totals,
            chartData: generateMortgageChartData(activeCurrency, totals)
        };
    }, [activeCurrency, mortgagesOverview]);

    useEffect(() => {
        refreshMortgagesOverview();
    }, []);

    if (!activeCurrency) return null;

    return (
        <Card>
            <Header title={<Link to="mortgages">Mortgages <FontAwesomeIcon icon={faChevronRight} /></Link>} level={1} />

            <LoadingError
                value={getMortgagesData()}
                loaded={data => (
                    <>
                        <div className={styles.breakdown}>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Balance
                                </div>
                                <div className={styles.percentage}>
                                    {!!data.totals.propertyValue &&
                                        <DisplayPercentage value={data.totals.balance / data.totals.propertyValue} minimumDP={1} maximumDP={1} />
                                    }
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.balance} />
                                </div>
                            </div>

                            <div className={classNames([styles.line])}>
                                <div className={styles.title}>
                                    Total Equity
                                </div>
                                <div className={styles.percentage}>
                                    {!!data.totals.equity && !!data.totals.propertyValue &&
                                        <DisplayPercentage value={data.totals.equity / data.totals.propertyValue} minimumDP={1} maximumDP={1} />
                                    }
                                </div>
                                <div className={styles.value}>
                                    {!!data.totals.equity &&
                                        <DisplayCurrency currencyCode={activeCurrency} value={data.totals.equity} />
                                    }
                                </div>
                            </div>

                            <div className={classNames([styles.line])}>
                                <div className={styles.title}>
                                    Total Property Value
                                </div>
                                <div className={styles.percentage}>
                                </div>
                                <div className={styles.value}>
                                    {!!data.totals.propertyValue &&
                                        <DisplayCurrency currencyCode={activeCurrency} value={data.totals.propertyValue} />
                                    }
                                </div>
                            </div>

                            {!!data.chartData &&
                                <Pie data={data.chartData} />
                            }
                        </div>
                    </>
                )}
            />
        </Card>

    );
};